import { forwardRef, type InputHTMLAttributes } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { IconButton } from './IconButton';
import { Input } from './Input';
import './SearchInput.css';

export interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** Controlled value; required so the clear affordance can appear. */
  value: string;
  /** Called when the clear button is pressed (sets value to ""). */
  onClear?: () => void;
  size?: 'sm' | 'md';
}

/**
 * Search field: leading magnifier, type="search", and a clear button that
 * appears once there is a value. Debounce in the caller (see useDebouncedValue)
 * to avoid a query per keystroke.
 *
 * Uma busca raramente tem rótulo visível: o placeholder é o único texto nela, e
 * placeholder não é nome acessível. Então ele vira também o `aria-label`, a não
 * ser que quem chama nomeie o campo (`aria-label`) ou aponte um rótulo
 * (`aria-labelledby`).
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  {
    value,
    onClear,
    placeholder = 'Buscar...',
    size = 'md',
    className,
    'aria-label': ariaLabel,
    ...rest
  },
  ref,
) {
  return (
    <Input
      ref={ref}
      type="search"
      role="searchbox"
      value={value}
      placeholder={placeholder}
      aria-label={ariaLabel ?? (rest['aria-labelledby'] ? undefined : placeholder)}
      size={size}
      className={cn('search-input', className)}
      leadingIcon={<Search />}
      trailingSlot={
        value ? (
          // O IconButton compartilhado traz o alvo de toque de 40px que o botão
          // de 22px não tinha.
          <IconButton size="sm" label="Limpar busca" icon={<X />} onClick={onClear} />
        ) : null
      }
      {...rest}
    />
  );
});
